import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEO from "../../components/SEO";

// Used only for the /kurs-sozlesmesi/preview dev/QA route - no API call is
// made for that token, so design/content review doesn't need a real record.
const SAMPLE_DATA = {
  participantName: "Max Mustermann",
  address: "Musterstraße 12",
  postalCodeCity: "10115 Berlin",
  phone: "+49 176 12345678",
  email: "max.mustermann@example.com",
  courseName: "Theater für Kinder (8-12 Jahre)",
  dayTime: "Dienstag, 16:00 - 17:30 Uhr",
  ageGroup: "8-12 Jahre",
  contractType: "unbefristet" as "befristet" | "unbefristet",
  contractEndDate: "31.07.2026",
  monthlyFee: "€110",
};

type SubmitStatus = "idle" | "submitting" | "done";
type PageState = "loading" | "error" | "ready";

interface CourseContractApiResponse {
  participant: {
    name: string;
    address: string | null;
    postal_code_city: string | null;
    phone: string | null;
    email: string;
  };
  course: {
    name: string;
    day_time: string | null;
    age_group: string | null;
  };
  contract: {
    contract_type: "befristet" | "unbefristet" | null;
    contract_end_date: string | null;
    monthly_fee_cents: number | null;
  };
  accepted: boolean;
  payment_setup_complete: boolean;
}

interface CourseContractAcceptResponse {
  message: string;
  already_accepted: boolean;
  checkout_url?: string;
  error?: string;
}

const formatDate = (isoDate: string | null) => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  return `${day}.${month}.${year}`;
};

const formatFee = (cents: number | null) => {
  if (cents === null) return "";
  return `€${(cents / 100).toFixed(2)}`;
};

const CourseContract: React.FC = () => {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const isPreview = token === "preview";

  const [pageState, setPageState] = useState<PageState>(isPreview ? "ready" : "loading");
  const [remoteData, setRemoteData] = useState<CourseContractApiResponse | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [earlyStartAccepted, setEarlyStartAccepted] = useState(false);
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [submitError, setSubmitError] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const [continueError, setContinueError] = useState(false);

  useEffect(() => {
    if (isPreview) return;

    setPageState("loading");

    fetch(`/api/kursvertrag/${token}?lang=${i18n.language}`, {
      headers: { Accept: "application/json" },
    })
      .then((res) => {
        if (!res.ok) throw new Error("invalid token");
        return res.json();
      })
      .then((json: CourseContractApiResponse) => {
        setRemoteData(json);
        setPageState("ready");
        if (json.accepted && json.payment_setup_complete) setStatus("done");
      })
      .catch(() => setPageState("error"));
  }, [token, isPreview, i18n.language]);

  const data = isPreview
    ? SAMPLE_DATA
    : remoteData
    ? {
        participantName: remoteData.participant.name,
        address: remoteData.participant.address || "",
        postalCodeCity: remoteData.participant.postal_code_city || "",
        phone: remoteData.participant.phone || "",
        email: remoteData.participant.email,
        courseName: remoteData.course.name,
        dayTime: remoteData.course.day_time || "",
        ageGroup: remoteData.course.age_group || "",
        contractType: remoteData.contract.contract_type || "unbefristet",
        contractEndDate: formatDate(remoteData.contract.contract_end_date),
        monthlyFee: formatFee(remoteData.contract.monthly_fee_cents),
      }
    : null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!accepted || !earlyStartAccepted) return;

    setStatus("submitting");
    setSubmitError(false);

    if (isPreview) {
      console.log("Course contract acceptance (preview stub, not sent anywhere):", {
        token,
        acceptedAt: new Date().toISOString(),
        earlyStartAcceptedAt: new Date().toISOString(),
      });
      setTimeout(() => setStatus("done"), 400);
      return;
    }

    fetch(`/api/kursvertrag/${token}/accept`, {
      method: "POST",
      headers: { Accept: "application/json" },
    })
      .then((res) => {
        if (!res.ok) throw new Error("accept failed");
        return res.json();
      })
      .then((json: CourseContractAcceptResponse) => {
        if (json.checkout_url) {
          window.location.href = json.checkout_url;
        } else {
          setStatus("done");
        }
      })
      .catch(() => {
        setSubmitError(true);
        setStatus("idle");
      });
  };

  const handleContinuePayment = () => {
    setContinuing(true);
    setContinueError(false);

    fetch(`/api/kursvertrag/${token}/accept`, {
      method: "POST",
      headers: { Accept: "application/json" },
    })
      .then((res) => {
        if (!res.ok) throw new Error("accept failed");
        return res.json();
      })
      .then((json: CourseContractAcceptResponse) => {
        if (json.checkout_url) {
          window.location.href = json.checkout_url;
        } else {
          setContinuing(false);
        }
      })
      .catch(() => {
        setContinueError(true);
        setContinuing(false);
      });
  };

  if (pageState === "loading") {
    return (
      <section className="w-full min-h-[60vh] flex flex-col items-center justify-center gap-4 px-4 py-12 bg-black text-white">
        <SEO title={t("kursVertrag.title")} noindex />
        <div
          className="w-8 h-8 rounded-full border-2 border-gray-600 border-t-brand-red-text animate-spin"
          role="status"
          aria-label={t("kursVertrag.loading")}
        />
        <p className="text-gray-300 text-center">{t("kursVertrag.loading")}</p>
      </section>
    );
  }

  if (pageState === "error" || !data) {
    return (
      <section className="w-full min-h-[60vh] flex justify-center items-center px-4 py-12 bg-black text-white">
        <SEO title={t("kursVertrag.title")} noindex />
        <p className="text-gray-300 text-center max-w-md">{t("kursVertrag.linkInvalid")}</p>
      </section>
    );
  }

  const paymentPending = !isPreview && remoteData?.accepted && !remoteData?.payment_setup_complete;

  if (paymentPending) {
    return (
      <section className="w-full min-h-[60vh] flex flex-col items-center justify-center gap-6 px-4 py-12 bg-black text-white text-center">
        <SEO title={t("kursVertrag.title")} noindex />
        <p className="text-gray-300 max-w-md">{t("kursVertrag.paymentPending")}</p>
        {continueError && (
          <p className="text-red-400 text-sm">{t("kursVertrag.submitError")}</p>
        )}
        <button
          type="button"
          onClick={handleContinuePayment}
          disabled={continuing}
          className="bg-[#D8001D] hover:bg-[#B8001A] text-white py-2 px-8 rounded font-semibold cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {continuing ? t("kursVertrag.acceptance.submitting") : t("kursVertrag.continuePayment")}
        </button>
      </section>
    );
  }

  return (
    <section className="w-full flex justify-center px-4 py-12 bg-black text-white">
      <div className="w-full max-w-4xl">
        <SEO
          title={t("kursVertrag.title")}
          description={t("kursVertrag.seoDescription")}
          keywords={t("kursVertrag.seoKeywords")}
          noindex
        />

        <h1 className="text-3xl font-extrabold underline underline-offset-8 decoration-brand-red-text mb-8 text-center">
          {t("kursVertrag.title")}
        </h1>

        <p className="text-gray-400 text-center mb-10">
          {t("kursVertrag.intro")}
        </p>

        {/* 1. Teilnehmer-/Erziehungsberechtigtendaten */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("kursVertrag.sections.participant.title")}
          </h2>
          <div className="bg-gray-800 p-6 rounded-lg text-center md:text-left space-y-2">
            <p className="text-gray-300">
              <span className="text-gray-400">
                {t("kursVertrag.sections.participant.nameLabel")}:{" "}
              </span>
              {data.participantName}
            </p>
            <p className="text-gray-300">
              <span className="text-gray-400">
                {t("kursVertrag.sections.participant.addressLabel")}:{" "}
              </span>
              {data.address}
            </p>
            <p className="text-gray-300">
              <span className="text-gray-400">
                {t("kursVertrag.sections.participant.postalCodeCityLabel")}:{" "}
              </span>
              {data.postalCodeCity}
            </p>
            <p className="text-gray-300">
              <span className="text-gray-400">
                {t("kursVertrag.sections.participant.phoneLabel")}:{" "}
              </span>
              {data.phone}
            </p>
            <p className="text-gray-300">
              <span className="text-gray-400">
                {t("kursVertrag.sections.participant.emailLabel")}:{" "}
              </span>
              {data.email}
            </p>
          </div>
        </div>

        {/* 2. Kursangaben */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("kursVertrag.sections.course.title")}
          </h2>
          <div className="bg-gray-800 p-6 rounded-lg text-center md:text-left space-y-2">
            <p className="text-gray-300">
              <span className="text-gray-400">
                {t("kursVertrag.sections.course.courseNameLabel")}:{" "}
              </span>
              {data.courseName}
            </p>
            <p className="text-gray-300">
              <span className="text-gray-400">
                {t("kursVertrag.sections.course.dayTimeLabel")}:{" "}
              </span>
              {data.dayTime}
            </p>
            <p className="text-gray-300">
              <span className="text-gray-400">
                {t("kursVertrag.sections.course.ageGroupLabel")}:{" "}
              </span>
              {data.ageGroup}
            </p>
            <p className="text-gray-300">
              <span className="text-gray-400">
                {t("kursVertrag.sections.course.contractTypeLabel")}:{" "}
              </span>
              {data.contractType === "befristet"
                ? t("kursVertrag.sections.course.contractTypeBefristet", {
                    endDate: data.contractEndDate,
                  })
                : t("kursVertrag.sections.course.contractTypeUnbefristet")}
            </p>
          </div>
        </div>

        {/* 3. Beiträge */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("kursVertrag.sections.fees.title")}
          </h2>
          <p className="text-gray-300 leading-relaxed text-center md:text-left">
            {t("kursVertrag.sections.fees.text", { monthlyFee: data.monthlyFee })}
          </p>
        </div>

        {/* 4. Zahlungsweise - SEPA-Lastschrift */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("kursVertrag.sections.payment.title")}
          </h2>
          <div className="space-y-4 text-gray-300 text-center md:text-left leading-relaxed">
            <p>{t("kursVertrag.sections.payment.p1")}</p>
            <p>{t("kursVertrag.sections.payment.p2")}</p>
            <p>{t("kursVertrag.sections.payment.p3")}</p>
            <p>{t("kursVertrag.sections.payment.p4")}</p>
          </div>
        </div>

        {/* 5. Kündigung / Vertragsänderungen */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("kursVertrag.sections.termination.title")}
          </h2>
          <div className="space-y-4 text-gray-300 text-center md:text-left leading-relaxed">
            <p>{t("kursVertrag.sections.termination.text1")}</p>
            <p>{t("kursVertrag.sections.termination.text2")}</p>
          </div>
        </div>

        {/* 6. Einverständniserklärung */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("kursVertrag.sections.consent.title")}
          </h2>
          <p className="text-gray-300 leading-relaxed text-center md:text-left mb-2">
            {t("kursVertrag.sections.consent.intro")}
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 md:ml-0 text-center md:text-left leading-relaxed">
            <li>{t("kursVertrag.sections.consent.item1")}</li>
            <li>{t("kursVertrag.sections.consent.item2")}</li>
            <li>{t("kursVertrag.sections.consent.item3")}</li>
          </ul>
          <p className="text-gray-300 leading-relaxed text-center md:text-left mt-2">
            {t("kursVertrag.sections.consent.closing")}
          </p>
        </div>

        {/* 7. Sonstiges */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("kursVertrag.sections.other.title")}
          </h2>
          <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 md:ml-0 text-center md:text-left leading-relaxed">
            <li>{t("kursVertrag.sections.other.substitute")}</li>
            <li>{t("kursVertrag.sections.other.liability")}</li>
          </ul>
        </div>

        {/* 8. Urheberrecht */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("kursVertrag.sections.copyright.title")}
          </h2>
          <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 md:ml-0 text-center md:text-left leading-relaxed">
            <li>{t("kursVertrag.sections.copyright.noRecording")}</li>
            <li>{t("kursVertrag.sections.copyright.privateUseOnly")}</li>
            <li>{t("kursVertrag.sections.copyright.courseContent")}</li>
          </ul>
        </div>

        {/* 9. Datenschutz */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("kursVertrag.sections.privacy.title")}
          </h2>
          <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 md:ml-0 text-center md:text-left leading-relaxed">
            <li>{t("kursVertrag.sections.privacy.ownPurpose")}</li>
            <li>{t("kursVertrag.sections.privacy.stripe")}</li>
          </ul>
          <p className="text-gray-300 text-center md:text-left mt-2">
            {t("kursVertrag.sections.privacy.moreInfo")}{" "}
            <a href="/datenschutz" className="text-brand-red-text underline">
              {t("kursVertrag.sections.privacy.linkText")}
            </a>
            .
          </p>
        </div>

        {/* 10. Widerrufsrecht */}
        {/* Muster-Widerrufsbelehrung per Anlage 1 EGBGB, filled in with association details — a lawyer sanity-check is still planned but not blocking. */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("kursVertrag.sections.withdrawal.title")}
          </h2>
          <div className="space-y-4 text-gray-300 text-center md:text-left leading-relaxed">
            <p>{t("kursVertrag.sections.withdrawal.rightIntro")}</p>
            <p>{t("kursVertrag.sections.withdrawal.howTo")}</p>
            <p>{t("kursVertrag.sections.withdrawal.timelyNotice")}</p>
            <h3 className="text-xl font-semibold mb-2 text-center">
              {t("kursVertrag.sections.withdrawal.consequencesTitle")}
            </h3>
            <p>{t("kursVertrag.sections.withdrawal.consequences1")}</p>
            <p>{t("kursVertrag.sections.withdrawal.consequences2")}</p>
          </div>
        </div>

        {/* 11. Allgemeine Geschäftsbedingungen */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("kursVertrag.sections.agb.title")}
          </h2>
          <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 md:ml-0 text-center md:text-left leading-relaxed">
            <li>{t("kursVertrag.sections.agb.registration")}</li>
            <li>{t("kursVertrag.sections.agb.minors")}</li>
            <li>{t("kursVertrag.sections.agb.priceChange")}</li>
            <li>{t("kursVertrag.sections.agb.feeOwedRegardless")}</li>
            <li>{t("kursVertrag.sections.agb.updateInfo")}</li>
          </ul>
        </div>

        {/* 12. Häufig gestellte Fragen */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("kursVertrag.sections.faq.title")}
          </h2>
          <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 md:ml-0 text-center md:text-left leading-relaxed">
            <li>{t("kursVertrag.sections.faq.item1")}</li>
            <li>{t("kursVertrag.sections.faq.item2")}</li>
            <li>{t("kursVertrag.sections.faq.item3")}</li>
            <li>{t("kursVertrag.sections.faq.item4")}</li>
            <li>{t("kursVertrag.sections.faq.item5")}</li>
          </ul>
        </div>

        {/* Acceptance */}
        <form onSubmit={handleSubmit} className="mt-12 border-t border-gray-800 pt-8">
          {status === "done" ? (
            <p className="text-white text-center font-semibold">
              {t("kursVertrag.acceptance.submit")} ✓
            </p>
          ) : (
            <>
              <div className="flex items-start gap-3 justify-center md:justify-start mb-6">
                <input
                  type="checkbox"
                  id="kursvertrag-accept"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  required
                  className="mt-1 w-4 h-4 accent-[#D8001D] text-[#D8001D] bg-gray-100 border-gray-300 rounded focus:ring-[#D8001D] focus:ring-2"
                />
                <label
                  htmlFor="kursvertrag-accept"
                  className="text-sm text-gray-300 leading-relaxed text-left"
                >
                  {t("kursVertrag.acceptance.label")}
                </label>
              </div>

              <div className="flex items-start gap-3 justify-center md:justify-start mb-6">
                <input
                  type="checkbox"
                  id="kursvertrag-early-start"
                  checked={earlyStartAccepted}
                  onChange={(e) => setEarlyStartAccepted(e.target.checked)}
                  required
                  className="mt-1 w-4 h-4 accent-[#D8001D] text-[#D8001D] bg-gray-100 border-gray-300 rounded focus:ring-[#D8001D] focus:ring-2"
                />
                <label
                  htmlFor="kursvertrag-early-start"
                  className="text-sm text-gray-300 leading-relaxed text-left"
                >
                  {t("kursVertrag.acceptance.earlyStartLabel")}
                </label>
              </div>

              {submitError && (
                <p className="text-red-400 text-sm text-center md:text-left mb-4">
                  {t("kursVertrag.submitError")}
                </p>
              )}

              <div className="flex justify-center md:justify-start">
                <button
                  type="submit"
                  disabled={!accepted || !earlyStartAccepted || status === "submitting"}
                  className="bg-[#D8001D] hover:bg-[#B8001A] text-white py-2 px-8 rounded font-semibold cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {status === "submitting"
                    ? t("kursVertrag.acceptance.submitting")
                    : t("kursVertrag.acceptance.submit")}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </section>
  );
};

export default CourseContract;
